import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  approvalSyncFailureReport,
  MOVE_TO_SET_MAX_IDS_PER_CALL,
  MOVE_TO_SET_TYPES,
  pipelineBatchUpdatesService,
  type MoveTestimonialsToSetResponse,
  type MoveToSetFamilyMateOutcome,
  type MoveToSetOutcome,
  type MoveToSetTarget,
  type MoveToSetType,
} from "../../../../api/pipelineBatchUpdates.service.js";
import { clampBatchSize, preventUnload } from "./batchRunHelpers.js";
import { describeApiError } from "./describeApiError.js";
import type { FamilyConflict } from "./findFamilyConflicts.js";

export type MoveToSetRunOutcome = MoveToSetOutcome | "unknown" | "not_sent";

export type MoveToSetRowKind = "requested" | "family_mate";

export type MoveToSetRunResult = {
  id: string;
  rowKind: MoveToSetRowKind;
  requestedId: string | null;
  testimonialType: MoveToSetType;
  outcome: MoveToSetRunOutcome;
  previousContactId: string | null;
  previousTestimonialType: string | null;
  error?: string;
};

export type MoveToSetFamilyLink = {
  mateId: string;
  requestedId: string;
  testimonialType: MoveToSetType;
  outcome: MoveToSetFamilyMateOutcome;
};

export type MoveToSetRunTotals = {
  moved: number;
  unchanged: number;
  notFound: number;
  failed: number;
  unknown: number;
  notSent: number;
  familyMatesMoved: number;
};

export type MoveToSetPhase = "checking" | "writing";

export type MoveToSetProgress = {
  batch: number;
  completedBatches: number;
  totalBatches: number;
  processedRows: number;
  unknownRows: number;
  totalRows: number;
  testimonialType: MoveToSetType | null;
  phase: MoveToSetPhase | null;
};

export type MoveToSetRunKind = "file" | "retry";

export type MoveToSetFamilyCheck = (links: MoveToSetFamilyLink[]) => FamilyConflict[];

type MoveToSetRunMode = { dryRun: true } | { dryRun: false; familyCheck: MoveToSetFamilyCheck };

export type MoveToSetBatchOptions = MoveToSetRunMode & { batchSize: number };

export type MoveToSetRunSummary = {
  finished: boolean;
  blocked: boolean;
  familyLinks: MoveToSetFamilyLink[];
};

type MoveToSetBatch = { testimonialType: MoveToSetType; ids: string[] };

type HardErrorKind = "rolled_back" | "rejected" | "unknown";

const EMPTY_PROGRESS: MoveToSetProgress = {
  batch: 0,
  completedBatches: 0,
  totalBatches: 0,
  processedRows: 0,
  unknownRows: 0,
  totalRows: 0,
  testimonialType: null,
  phase: null,
};

export function countByType(targets: MoveToSetTarget[]): Record<MoveToSetType, number> {
  const counts = { set_testimonial: 0, clerk_testimonial: 0 };
  for (const { testimonialType } of targets) counts[testimonialType] += 1;
  return counts;
}

export function failedTargets(results: MoveToSetRunResult[]): MoveToSetTarget[] {
  return results
    .filter(result => result.rowKind === "requested" && result.outcome === "failed")
    .map(({ id, testimonialType }) => ({ testimonialId: id, testimonialType }));
}

function planBatches(targets: MoveToSetTarget[], batchSize: number): MoveToSetBatch[] {
  return MOVE_TO_SET_TYPES.flatMap(testimonialType => {
    const ids = targets
      .filter(target => target.testimonialType === testimonialType)
      .map(target => target.testimonialId);
    const batches: MoveToSetBatch[] = [];
    for (let from = 0; from < ids.length; from += batchSize) {
      batches.push({ testimonialType, ids: ids.slice(from, from + batchSize) });
    }
    return batches;
  });
}

function tally(results: MoveToSetRunResult[]): MoveToSetRunTotals {
  const totals = {
    moved: 0,
    unchanged: 0,
    notFound: 0,
    failed: 0,
    unknown: 0,
    notSent: 0,
    familyMatesMoved: 0,
  };
  for (const { rowKind, outcome } of results) {
    if (rowKind === "family_mate") {
      if (outcome === "moved") totals.familyMatesMoved += 1;
    } else if (outcome === "moved") totals.moved += 1;
    else if (outcome === "unchanged") totals.unchanged += 1;
    else if (outcome === "not_found") totals.notFound += 1;
    else if (outcome === "unknown") totals.unknown += 1;
    else if (outcome === "not_sent") totals.notSent += 1;
    else totals.failed += 1;
  }
  return totals;
}

function mergeEntry(
  existing: MoveToSetRunResult | undefined,
  incoming: MoveToSetRunResult,
): MoveToSetRunResult {
  if (!existing) return incoming;
  const unconfirmed = (outcome: MoveToSetRunOutcome) =>
    outcome === "failed" || outcome === "unknown" || outcome === "not_sent";
  const keepExisting =
    (existing.outcome === "moved" &&
      (incoming.outcome === "unchanged" || unconfirmed(incoming.outcome))) ||
    (incoming.outcome === "not_sent" && !unconfirmed(existing.outcome));
  const winner = keepExisting ? existing : incoming;
  const firstSeen =
    existing.outcome === "unknown" || existing.outcome === "not_sent" ? incoming : existing;
  const requested = existing.rowKind === "requested" || incoming.rowKind === "requested";
  return {
    ...winner,
    rowKind: requested ? "requested" : "family_mate",
    requestedId: requested ? null : winner.requestedId,
    previousContactId: firstSeen.previousContactId,
    previousTestimonialType: firstSeen.previousTestimonialType,
  };
}

function mergeInto(
  previous: MoveToSetRunResult[],
  incoming: MoveToSetRunResult[],
): MoveToSetRunResult[] {
  const byId = new Map(previous.map(result => [result.id, result]));
  for (const entry of incoming) byId.set(entry.id, mergeEntry(byId.get(entry.id), entry));
  return [...byId.values()];
}

function reportEntries(report: MoveTestimonialsToSetResponse): MoveToSetRunResult[] {
  const { testimonialType } = report;
  return [
    ...report.results.map(
      (result): MoveToSetRunResult => ({
        ...result,
        rowKind: "requested",
        requestedId: null,
        testimonialType,
      }),
    ),
    ...report.familyMates.map(
      (mate): MoveToSetRunResult => ({ ...mate, rowKind: "family_mate", testimonialType }),
    ),
  ];
}

function unknownEntries({ testimonialType, ids }: MoveToSetBatch): MoveToSetRunResult[] {
  return ids.map(id => ({
    id,
    rowKind: "requested",
    requestedId: null,
    testimonialType,
    outcome: "unknown",
    previousContactId: null,
    previousTestimonialType: null,
    error: "Batch state unknown, re-run to confirm",
  }));
}

function describeConflict({ mateId, requestedId, reason }: FamilyConflict): string {
  return `${mateId} found through ${requestedId}: ${reason}`;
}

function notSentReason(id: string, conflicts: FamilyConflict[]): string {
  const own = conflicts.filter(conflict => conflict.mateId === id || conflict.requestedId === id);
  if (own.length > 0) {
    return `Not sent, blocking family conflict: ${own.map(describeConflict).join("; ")}`;
  }
  const more = conflicts.length > 1 ? ` (and ${conflicts.length - 1} more)` : "";
  return `Not sent, the batch was stopped by a blocking family conflict: ${describeConflict(conflicts[0])}${more}`;
}

function notSentEntries(
  { testimonialType, ids }: MoveToSetBatch,
  conflicts: FamilyConflict[],
): MoveToSetRunResult[] {
  return ids.map(id => ({
    id,
    rowKind: "requested",
    requestedId: null,
    testimonialType,
    outcome: "not_sent",
    previousContactId: null,
    previousTestimonialType: null,
    error: notSentReason(id, conflicts),
  }));
}

function familyLinksOf(report: MoveTestimonialsToSetResponse): MoveToSetFamilyLink[] {
  return report.familyMates.map(mate => ({
    mateId: mate.id,
    requestedId: mate.requestedId,
    testimonialType: report.testimonialType,
    outcome: mate.outcome,
  }));
}

function hardErrorKind(err: unknown): HardErrorKind {
  const status = err instanceof Error ? (err as Error & { status?: unknown }).status : undefined;
  if (status === 500) return "rolled_back";
  if (typeof status === "number" && status >= 400 && status <= 499) return "rejected";
  return "unknown";
}

const REST_NOT_SENT = "Earlier batches are written and later ones were not sent.";

function batchLabel(batch: number, totalBatches: number, { testimonialType, ids }: MoveToSetBatch) {
  return `Batch ${batch} / ${totalBatches} (${ids.length} ${testimonialType} rows)`;
}

function preCheckFailureMessage(
  err: unknown,
  batch: number,
  totalBatches: number,
  current: MoveToSetBatch,
): string {
  const detail = describeApiError(err, "Move to set failed");
  return `${batchLabel(batch, totalBatches, current)} stopped: the family pre-check failed: ${detail}. Nothing was written for this batch. ${REST_NOT_SENT}`;
}

function blockedMessage(
  batch: number,
  totalBatches: number,
  current: MoveToSetBatch,
  blocking: number,
): string {
  const conflicts = `${blocking} blocking family ${blocking === 1 ? "conflict" : "conflicts"}`;
  return `${batchLabel(batch, totalBatches, current)} was not sent: the family pre-check found ${conflicts} that the dry run did not show. Nothing was written for this batch. ${REST_NOT_SENT} Fix the file and run the dry run again.`;
}

function batchFailureMessage(
  err: unknown,
  batch: number,
  totalBatches: number,
  current: MoveToSetBatch,
  kind: HardErrorKind | "dry_run",
): string {
  const detail = describeApiError(err, "Move to set failed");
  const failure = `${batchLabel(batch, totalBatches, current)} failed: ${detail}.`;
  const rest = REST_NOT_SENT;
  const rerun = "Re-running the same file is safe: rows already moved come back as unchanged.";
  if (kind === "dry_run") return `${failure} Nothing was written (dry run).`;
  if (kind === "rejected") {
    return `${failure} The request was rejected, so nothing was written for this batch. ${rest}`;
  }
  if (kind === "rolled_back") {
    return `${failure} A 500 means this batch's pipeline transaction rolled back, so its rows were not moved in the pipeline. approval-api may already show the change. ${rest} ${rerun}`;
  }
  return `${failure} This batch's state is unknown: the pipeline may have committed it before the error. Its ${current.ids.length} rows are listed as unknown, re-run the same file to confirm. ${rest} ${rerun}`;
}

export function useMoveToSetBatch() {
  const [running, setRunning] = useState(false);
  const [runKind, setRunKind] = useState<MoveToSetRunKind>("file");
  const [stopped, setStopped] = useState(false);
  const [dryRun, setDryRun] = useState(true);
  const [progress, setProgress] = useState<MoveToSetProgress>(EMPTY_PROGRESS);
  const [results, setResults] = useState<MoveToSetRunResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [auditEmitFailed, setAuditEmitFailed] = useState(false);
  const [blockedBy, setBlockedBy] = useState<FamilyConflict[]>([]);
  const stopRef = useRef(false);
  const resultsRef = useRef<MoveToSetRunResult[]>([]);

  useEffect(() => {
    if (!running) return;
    window.addEventListener("beforeunload", preventUnload);
    return () => window.removeEventListener("beforeunload", preventUnload);
  }, [running]);

  const commitResults = useCallback((next: MoveToSetRunResult[]) => {
    resultsRef.current = next;
    setResults(next);
  }, []);

  const stop = useCallback(() => {
    stopRef.current = true;
  }, []);

  const reset = useCallback(() => {
    setRunKind("file");
    setStopped(false);
    setDryRun(true);
    setProgress(EMPTY_PROGRESS);
    commitResults([]);
    setError(null);
    setAuditEmitFailed(false);
    setBlockedBy([]);
  }, [commitResults]);

  const run = useCallback(
    async (batches: MoveToSetBatch[], mode: MoveToSetRunMode): Promise<MoveToSetRunSummary> => {
      const totalBatches = batches.length;
      const totalRows = batches.reduce((total, batch) => total + batch.ids.length, 0);
      const familyLinks: MoveToSetFamilyLink[] = [];
      const halted = (blocked: boolean) => ({ finished: false, blocked, familyLinks });

      stopRef.current = false;
      setRunning(true);
      setStopped(false);
      setDryRun(mode.dryRun);
      setError(null);
      setBlockedBy([]);
      setProgress({ ...EMPTY_PROGRESS, totalBatches, totalRows });

      let processedRows = 0;
      let completedBatches = 0;

      try {
        for (const [index, current] of batches.entries()) {
          if (stopRef.current) {
            setStopped(true);
            return halted(false);
          }
          const batch = index + 1;
          const { testimonialType, ids } = current;
          const at = { batch, totalBatches, totalRows, testimonialType, unknownRows: 0 };

          if (!mode.dryRun) {
            setProgress({ ...at, completedBatches, processedRows, phase: "checking" });
            let preCheck: MoveTestimonialsToSetResponse;
            try {
              preCheck = await pipelineBatchUpdatesService.moveTestimonialsToSet({
                ids,
                testimonialType,
                dryRun: true,
              });
            } catch (err) {
              setError(preCheckFailureMessage(err, batch, totalBatches, current));
              return halted(false);
            }
            const blocking = mode
              .familyCheck([...familyLinks, ...familyLinksOf(preCheck)])
              .filter(conflict => conflict.severity === "blocking");
            if (blocking.length > 0) {
              commitResults(mergeInto(resultsRef.current, notSentEntries(current, blocking)));
              setBlockedBy(blocking);
              setError(blockedMessage(batch, totalBatches, current, blocking.length));
              return halted(true);
            }
            if (stopRef.current) {
              setStopped(true);
              return halted(false);
            }
          }

          const phase = mode.dryRun ? null : "writing";
          setProgress({ ...at, completedBatches, processedRows, phase });

          let report: MoveTestimonialsToSetResponse;
          try {
            report = await pipelineBatchUpdatesService.moveTestimonialsToSet({
              ids,
              testimonialType,
              dryRun: mode.dryRun,
            });
          } catch (err) {
            const partial = approvalSyncFailureReport(err);
            if (!partial) {
              const kind = mode.dryRun ? "dry_run" : hardErrorKind(err);
              if (kind === "unknown") {
                commitResults(mergeInto(resultsRef.current, unknownEntries(current)));
                setProgress({
                  ...at,
                  completedBatches,
                  processedRows,
                  phase,
                  unknownRows: ids.length,
                });
              }
              setError(batchFailureMessage(err, batch, totalBatches, current, kind));
              return halted(false);
            }
            report = partial;
          }

          commitResults(mergeInto(resultsRef.current, reportEntries(report)));
          familyLinks.push(...familyLinksOf(report));
          if (report.auditEmitFailed) setAuditEmitFailed(true);
          processedRows += ids.length;
          completedBatches = batch;
          setProgress({ ...at, completedBatches, processedRows, phase });
        }
        return { finished: true, blocked: false, familyLinks };
      } finally {
        setRunning(false);
      }
    },
    [commitResults],
  );

  const start = useCallback(
    async (targets: MoveToSetTarget[], options: MoveToSetBatchOptions) => {
      setRunKind("file");
      commitResults([]);
      setAuditEmitFailed(false);
      const batchSize = clampBatchSize(options.batchSize, MOVE_TO_SET_MAX_IDS_PER_CALL);
      return run(planBatches(targets, batchSize), options);
    },
    [commitResults, run],
  );

  const retryFailed = useCallback(
    async (
      batchSize: number,
      familyCheck: MoveToSetFamilyCheck,
    ): Promise<MoveToSetRunSummary | null> => {
      const targets = failedTargets(resultsRef.current);
      if (targets.length === 0) return null;
      setRunKind("retry");
      return run(planBatches(targets, clampBatchSize(batchSize, MOVE_TO_SET_MAX_IDS_PER_CALL)), {
        dryRun: false,
        familyCheck,
      });
    },
    [run],
  );

  const totals = useMemo(() => tally(results), [results]);

  return useMemo(
    () => ({
      running,
      runKind,
      stopped,
      dryRun,
      progress,
      results,
      totals,
      error,
      auditEmitFailed,
      blockedBy,
      start,
      retryFailed,
      stop,
      reset,
    }),
    [
      running,
      runKind,
      stopped,
      dryRun,
      progress,
      results,
      totals,
      error,
      auditEmitFailed,
      blockedBy,
      start,
      retryFailed,
      stop,
      reset,
    ],
  );
}
